import { emptyIfInstant } from "./mockMode";

/**
 * Mock data for the inventory + warehouses module.
 *
 * Mirrors the shapes defined in `instant.schema.ts`.
 */

const NOW = new Date("2026-05-09T08:00:00Z").getTime();
const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

export type WarehouseType =
  | "raw_material"
  | "finished_goods"
  | "transit"
  | "dealer_consignment";

export type StockMovementType =
  | "purchase_receipt"
  | "sales_shipment"
  | "manufacturing_consumption"
  | "manufacturing_output"
  | "transfer_in"
  | "transfer_out"
  | "adjustment"
  | "sales_return"
  | "purchase_return";

export type ProductKind = "manufactured" | "imported" | "service";

export type MockBranch = {
  _id: string;
  code: string;
  displayName: string;
  branchType: "hq" | "factory" | "sales";
  /** Bangladesh district / city. */
  city: string;
  addressLine: string;
  contactPhone: string | null;
  isActive: boolean;
};

export const mockBranches: MockBranch[] = emptyIfInstant<MockBranch>([]);

/** Helper: branch lookup by id. Returns null if not found. */
export function findBranch(id: string): MockBranch | null {
  return mockBranches.find((b) => b._id === id) ?? null;
}

export type MockWarehouse = {
  _id: string;
  branchId: string;
  code: string;
  displayName: string;
  warehouseType: WarehouseType;
  isActive: boolean;
};

export const mockWarehouses: MockWarehouse[] = emptyIfInstant<MockWarehouse>([]);

export type MockProduct = {
  _id: string;
  sku: string;
  displayName: string;
  productKind: ProductKind;
  primaryUnitId: string;
  reorderQuantity: number | null;
  isActive: boolean;
};

export const mockProducts: MockProduct[] = emptyIfInstant<MockProduct>([]);

export type MockProductVariant = {
  _id: string;
  productId: string;
  variantSku: string;
  variantName: string;
};

export const mockProductVariants: MockProductVariant[] = emptyIfInstant<MockProductVariant>([]);

export type MockStockMovement = {
  _id: string;
  productId: string;
  productVariantId: string | null;
  warehouseId: string;
  movementType: StockMovementType;
  /** Signed delta. Positive = in, negative = out. */
  quantityDelta: number;
  quantityBalanceAfter: number;
  /** Per-unit cost snapshot in BDT cents. */
  unitCostBaseCents: bigint;
  /** Signed total value moved (positive when stock added). */
  valueDeltaBaseCents: bigint;
  referenceType: string | null;
  referenceId: string | null;
  occurredAt: number;
  notes: string | null;
};

/**
 * Stock movements — six baseline rows plus
 * three more so the dashboard renders interesting numbers (transfers,
 * recent activity, low-stock and reorder cases).
 */
export const mockStockMovements: MockStockMovement[] = emptyIfInstant<MockStockMovement>([]);

export type MockStockReservation = {
  _id: string;
  productId: string;
  productVariantId: string | null;
  warehouseId: string;
  quantityReserved: number;
  status: "active" | "released" | "consumed";
  referenceType: string;
  referenceId: string;
};

export const mockStockReservations: MockStockReservation[] = emptyIfInstant<MockStockReservation>([]);

/** One opening-stock position: product × warehouse × qty × cost. */
export type MockOpeningStock = {
  /** Stable lineId — mirrors the stockLedger row. */
  lineId: string;
  productId: string;
  warehouseId: string;
  /** Quantity on hand at the cut-over date. */
  quantityOnHand: number;
  /** Cost per unit in BDT base cents (smallest unit). */
  unitCostBaseCents: bigint;
  /** Total inventory value = quantityOnHand × unitCostBaseCents. */
  totalValueBaseCents: bigint;
  /** Cut-over as-of date (epoch ms). Defaults to global opening config date. */
  asOfDate: number | null;
};

const OPENING_AS_OF = new Date("2026-01-01T00:00:00Z").getTime();

export const mockOpeningStockEntries: MockOpeningStock[] = emptyIfInstant<MockOpeningStock>([]);
// Total inventory opening value: 180,000 + 176,000 + 450,000 + 9,000 = 815,000 BDT
